import { AxiosRequestConfig } from 'axios';
import { fetchWithToken } from 'api/data';
import routes from 'api/routes';
import {
  Dashboard,
  CreateDashboardRequest,
  UpdateDashboardData,
  DashboardWidget,
  WidgetType,
  DashboardTemplateUI as DashboardTemplate,
  CreateWidgetRequest,
  UpdateWidgetRequest,
  CreateFromTemplateRequest,
  ObjectData,
  DeviceData,
  ParamData,
  DashboardsResponse,
} from 'types/dashboard.ui';

export const dashboardAPI = {
  // Получить список дашбордов
  getDashboards: (params?: { 
    page?: number; 
    per_page?: number; 
    search?: string; 
  }): Promise<DashboardsResponse> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardManagement(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Получить дашборд по ID
  getDashboard: (id: string): Promise<Dashboard> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardManagementById(id),
      method: 'get',
    };
    
    return fetchWithToken(config)
      .then((response) => {
        const dashboardData = response.data.data;
        // Убеждаемся, что widgets всегда является массивом
        // Бэкенд возвращает поле "Widgets" с заглавной буквы
        return {
          ...dashboardData,
          widgets: dashboardData.Widgets || dashboardData.widgets || []
        };
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Создать новый дашборд
  createDashboard: (data: CreateDashboardRequest): Promise<Dashboard> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardManagement(),
      method: 'post',
      data,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Обновить дашборд
  updateDashboard: (id: string, data: UpdateDashboardData): Promise<Dashboard> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardManagementById(id),
      method: 'put',
      data,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Удалить дашборд
  deleteDashboard: (id: string): Promise<void> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardManagementById(id),
      method: 'delete',
    };
    
    return fetchWithToken(config)
      .then(() => {})
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Получить статистику дашбордов
  getDashboardStatistics: () => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardStatistics(),
      method: 'get',
    };
    
    return fetchWithToken(config)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Получить данные для виджетов
  getWidgetData: (widgetType: string, widgetConfig: Record<string, any>) => {
    // В зависимости от типа виджета вызываем разные endpoints
    switch (widgetType) {
      case 'chart':
        if (widgetConfig.deviceId && widgetConfig.archive) {
          const config: AxiosRequestConfig = {
            url: routes.deviceChartData(
              widgetConfig.deviceId,
              widgetConfig.archive,
              widgetConfig.limit || 100,
              widgetConfig.dateStart ? new Date(widgetConfig.dateStart) : null,
              widgetConfig.dateEnd ? new Date(widgetConfig.dateEnd) : null
            ),
            method: 'get',
          };
          return fetchWithToken(config)
            .then((response) => response.data)
            .catch((error) => {
              console.error(error);
              return [];
            });
        }
        break;
      
      case 'table':
      case 'gauge':
        if (widgetConfig.deviceGroup) {
          const config: AxiosRequestConfig = {
            url: routes.deviceDataFlow(
              widgetConfig.deviceGroup, 
              true, 
              widgetConfig.valueDate ? new Date(widgetConfig.valueDate) : undefined
            ),
            method: 'get',
          };
          return fetchWithToken(config)
            .then((response) => response.data)
            .catch((error) => {
              console.error(error);
              return [];
            });
        }
        break;
      
      case 'schema':
        if (widgetConfig.industryType) {
          const config: AxiosRequestConfig = {
            url: routes.objectMap(widgetConfig.industryType),
            method: 'get',
          };
          return fetchWithToken(config)
            .then((response) => response.data)
            .catch((error) => {
              console.error(error);
              return [];
            });
        }
        break;
      
      default:
        return Promise.resolve([]);
    }
    return Promise.resolve([]);
  },

  // Widget Management API
  createWidget: (data: CreateWidgetRequest): Promise<DashboardWidget> => {
    const config: AxiosRequestConfig = {
      url: routes.widgets(),
      method: 'post',
      data,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  updateWidget: (id: string, data: UpdateWidgetRequest): Promise<DashboardWidget> => {
    // Убираем поле id из данных, так как оно передается в URL
    const { id: widgetId, ...widgetData } = data;
    
    console.log('updateWidget API call:', {
      widgetId: id,
      widgetData,
      url: routes.widgetById(id)
    });
    
    const config: AxiosRequestConfig = {
      url: routes.widgetById(id),
      method: 'put',
      data: widgetData, // Отправляем данные напрямую без вложения в { id, data }
    };
    
    return fetchWithToken(config)
      .then((response) => {
        console.log('updateWidget success:', response.data);
        return response.data.data;
      })
      .catch((error) => {
        console.error('updateWidget error:', error.response?.data || error);
        throw error;
      });
  },

  // Обновление позиций нескольких виджетов одновременно
  updateWidgetsPositions: (dashboardId: string, widgets: Array<{
    id: string;
    position_x: number;
    position_y: number;
    width: number;
    height: number;
  }>): Promise<void> => {
    const requestData = {
      widgets: widgets.map(w => ({
        id: w.id,
        position_x: w.position_x,
        position_y: w.position_y,
        width: w.width,
        height: w.height,
      }))
    };
    
    console.log('updateWidgetsPositions API call:', {
      dashboardId,
      url: routes.dashboardWidgetPositions(dashboardId),
      requestData: JSON.stringify(requestData, null, 2)
    });
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardWidgetPositions(dashboardId),
      method: 'put',
      data: requestData,
    };
    
    return fetchWithToken(config)
      .then((response) => {
        console.log('updateWidgetsPositions success:', response.data);
        return response.data;
      })
      .catch((error) => {
        console.error('updateWidgetsPositions error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        throw error;
      });
  },

  deleteWidget: (id: string): Promise<void> => {
    const config: AxiosRequestConfig = {
      url: routes.widgetById(id),
      method: 'delete',
    };
    
    return fetchWithToken(config)
      .then(() => {})
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Template API
  getTemplates: (): Promise<DashboardTemplate[]> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardTemplates(),
      method: 'get',
    };
    
    return fetchWithToken(config)
      .then((response) => {
        const templatesData = response.data.data;
        // Убеждаемся, что возвращается массив
        return Array.isArray(templatesData) ? templatesData : [];
      })
      .catch((error) => {
        console.error('Error loading templates:', error);
        // Возвращаем пустой массив в случае ошибки
        return [];
      });
  },

  createFromTemplate: (data: CreateFromTemplateRequest): Promise<Dashboard> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardFromTemplate(),
      method: 'post',
      data: {
        template_id: data.template_id,
        name: data.name,
        description: data.description,
        is_public: data.is_public,
      },
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Widget Types API
  getWidgetTypes: (): Promise<WidgetType[]> => {
    const config: AxiosRequestConfig = {
      url: routes.widgetTypes(),
      method: 'get',
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Data Source API
  getObjects: (companyId?: string, search?: string): Promise<ObjectData[]> => {
    const params: Record<string, string> = {};
    if (companyId) params.company_id = companyId;
    if (search) params.search = search;
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardDataObjects(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getDevices: (objectId?: string, search?: string): Promise<DeviceData[]> => {
    const params: Record<string, string> = {};
    if (objectId) params.object_id = objectId;
    if (search) params.search = search;
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardDataDevices(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getParams: (deviceId?: string, search?: string): Promise<ParamData[]> => {
    const params: Record<string, string> = {};
    if (deviceId) params.device_id = deviceId;
    if (search) params.search = search;
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardDataParams(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Historical data for charts
  getHistoricalData: (
    deviceId: string,
    archiveType: string,
    paramIds: string[],
    startTime?: string,
    endTime?: string,
    limit?: number
  ): Promise<any[]> => {
    const params: Record<string, any> = {
      device_id: deviceId,
      archive_type: archiveType,
      param_ids: paramIds,
    };
    if (startTime) params.start_time = startTime;
    if (endTime) params.end_time = endTime;
    if (limit) params.limit = limit;
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardDataHistorical(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data) // Извлекаем data.data
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  // Legacy method for backward compatibility - используется виджетами gauge, table, schema, map
  // которые получают данные без указания конкретного device_id
  getHistoricalDataByParams: (
    paramIds: string[],
    startTime: string,
    endTime: string
  ): Promise<any[]> => {
    const params: Record<string, any> = {
      param_ids: paramIds,
      start_time: startTime,
      end_time: endTime,
    };
    
    const config: AxiosRequestConfig = {
      url: routes.dashboardDataHistorical(),
      method: 'get',
      params,
    };
    
    return fetchWithToken(config)
      .then((response) => response.data.data || [])
      .catch((error) => {
        console.error(error);
        return [];
      });
  },

  // Batch operations for performance
  updateWidgetPositions: (dashboardId: string, updates: { id: string; position: { x: number; y: number; w: number; h: number } }[]): Promise<void> => {
    const config: AxiosRequestConfig = {
      url: routes.dashboardWidgetPositions(dashboardId),
      method: 'put',
      data: { widgets: updates },
    };
    
    return fetchWithToken(config)
      .then(() => {})
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },
};